/**
 * Tap-leave feedback: product cards and default buttons show they are
 * opening a new page. Safari unloads before a click-time class can paint,
 * so the state starts on pointer/touch and cancels if the finger scrolls.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
  if (typeof window !== "undefined") {
    api.bindTapLeave(window);
  }
})(typeof window !== "undefined" ? window : this, function () {
  var MOVE_CANCEL_PX2 = 144;
  var LEAVE_GIVE_UP_MS = 8000;
  var CARD_CONTROL =
    "button, input, select, textarea, product-form, .f-product-form, .product-card__atc, .product-card__action-button, .product-card__quick-view, .color-swatch, [data-quick-view]";
  var SKIP_ROOT =
    '[data-tap-leave="off"], .f-mobile-nav, .f-drawer-mobile-nav, .wheelbuilder, .wheelbuilder-panel, .drawer-cta-compact, .f-button--scroll-top';
  var SKIP_BUTTON =
    ".btn--plain, .btn--link, .btn--underline, .btn-loadmore, .btn--loading, .product-card__atc, .product-card__action-button, .product-card__quick-view";
  var SPINNER_HTML =
    '<span class="f-spinner-icon f-spinner-icon--leave" aria-hidden="true"><svg focusable="false" role="presentation" class="f-icon-svg f-icon--medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>';

  function isSamePageUrl(href, here) {
    if (!href || href === "#" || href.indexOf("javascript:") === 0) return true;
    try {
      var next = new URL(href, here.href);
      return (
        next.origin === here.origin &&
        next.pathname === here.pathname &&
        next.search === here.search
      );
    } catch (err) {
      return true;
    }
  }

  function isLeaveHref(href, here) {
    return !isSamePageUrl(href, here);
  }

  function eventElement(event) {
    if (event.composedPath) {
      var path = event.composedPath();
      for (var i = 0; i < path.length; i++) {
        if (path[i] && path[i].nodeType === 1) return path[i];
      }
    }
    return event.target && event.target.nodeType === 1
      ? event.target
      : event.target && event.target.parentElement;
  }

  function eventPoint(event) {
    if (event.touches && event.touches[0]) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    if (event.clientX != null) return { x: event.clientX, y: event.clientY };
    return null;
  }

  function modifiedClick(event) {
    return !!(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
  }

  function skipRoot(node) {
    return !!(node && node.closest && node.closest(SKIP_ROOT));
  }

  function findCardLeave(target) {
    if (!target || !target.closest) return null;
    if (skipRoot(target)) return null;
    if (target.closest(CARD_CONTROL)) return null;
    var card = target.closest(".product-card");
    if (!card) return null;
    var link = target.closest("a[href]");
    if (!link || !card.contains(link)) {
      link = card.querySelector("a.product-card__link[href], a.product-card__wheels-build[href]");
    }
    if (!link) return null;
    if (link.target === "_blank" || link.hasAttribute("download")) return null;
    return { card: card, link: link };
  }

  function isCheckoutButton(node) {
    if (!node || !node.closest) return null;
    return node.closest(
      '[name="checkout"], [data-checkout-button], .f-cart-drawer__checkout-btn, .cart__checkout-button'
    );
  }

  function findButtonLeave(target) {
    if (!target || !target.closest) return null;
    if (skipRoot(target)) return null;
    if (target.closest(".product-card")) return null;
    var checkout = isCheckoutButton(target);
    if (checkout) {
      if (checkout.disabled || checkout.getAttribute("aria-disabled") === "true") {
        return null;
      }
      return checkout;
    }
    var btn = target.closest("a.btn[href]");
    if (!btn) return null;
    if (btn.matches(SKIP_BUTTON)) return null;
    if (btn.target === "_blank" || btn.hasAttribute("download")) return null;
    return btn;
  }

  function ensurePageProgress(doc) {
    var barHost = doc.querySelector(".f-leave-progress");
    if (!barHost) {
      barHost = doc.createElement("div");
      barHost.className = "f-leave-progress";
      barHost.setAttribute("aria-hidden", "true");
      barHost.innerHTML = '<span class="f-leave-progress-bar"></span>';
      doc.body.appendChild(barHost);
    }
    return barHost;
  }

  function restartBar(bar) {
    if (!bar) return;
    bar.style.webkitAnimation = "none";
    bar.style.animation = "none";
    void bar.offsetWidth;
    bar.style.webkitAnimation = "";
    bar.style.animation = "";
  }

  function ensureButtonSpinner(btn) {
    if (!btn || btn.querySelector(".f-spinner-icon")) return;
    btn.insertAdjacentHTML("beforeend", SPINNER_HTML);
  }

  function submitCheckoutLeave(btn, doc) {
    if (!btn || (btn.getAttribute && btn.getAttribute("data-checkout-leave") === "1")) {
      return false;
    }
    var form = btn.form;
    if (!form && btn.getAttribute && doc && doc.getElementById) {
      var formId = btn.getAttribute("form");
      if (formId) form = doc.getElementById(formId);
    }
    if (!form) return false;
    if (btn.setAttribute) btn.setAttribute("data-checkout-leave", "1");
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit(btn);
      return true;
    }
    if (typeof form.submit === "function") {
      var holder = doc && doc.createElement ? doc.createElement("input") : null;
      if (holder) {
        holder.type = "hidden";
        holder.name = "checkout";
        holder.value = "Checkout";
        form.appendChild(holder);
      }
      form.submit();
      return true;
    }
    return false;
  }

  function bindTapLeave(win) {
    if (!win || !win.document || win.__tapLeaveBound) return;
    win.__tapLeaveBound = true;
    var doc = win.document;
    var leaving = null;
    var leaveTimer = null;

    function clearLeave() {
      if (leaveTimer) {
        win.clearTimeout(leaveTimer);
        leaveTimer = null;
      }
      if (!leaving) return;
      var card = leaving.card;
      var btn = leaving.btn;
      leaving = null;
      doc.documentElement.classList.remove("is-leaving-page");
      doc.body.classList.remove("is-leaving-page");
      doc.documentElement.removeAttribute("aria-busy");
      if (card) {
        card.classList.remove("is-opening");
        card.removeAttribute("aria-busy");
      }
      if (btn) {
        btn.classList.remove("btn--loading", "btn--leaving");
        btn.removeAttribute("aria-busy");
      }
      var host = doc.querySelector(".f-leave-progress");
      if (host) host.classList.remove("is-active");
    }

    /* The give-up path means the page plainly never left. A checkout button also loses its
       submitted marker here, because that marker makes the next tap refuse to submit while
       the CSS still takes pointer events away — the button would look alive but be dead.
       Only this path clears it; a cancelled press leaves an in-flight submit alone so a
       shopper can never be charged twice. */
    function giveUpLeave() {
      var btn = leaving && leaving.btn;
      if (btn && btn.removeAttribute) btn.removeAttribute("data-checkout-leave");
      clearLeave();
    }

    function beginLeave(next) {
      clearLeave();
      leaving = next;
      var barHost = ensurePageProgress(doc);
      doc.documentElement.classList.add("is-leaving-page");
      doc.body.classList.add("is-leaving-page");
      doc.documentElement.setAttribute("aria-busy", "true");
      if (next.card) {
        next.card.classList.add("is-opening");
        next.card.setAttribute("aria-busy", "true");
      }
      if (next.btn) {
        ensureButtonSpinner(next.btn);
        next.btn.classList.add("btn--loading", "btn--leaving");
        next.btn.setAttribute("aria-busy", "true");
      }
      barHost.classList.add("is-active");
      restartBar(barHost.querySelector(".f-leave-progress-bar"));
      if (next.card) void next.card.offsetWidth;
      if (next.btn) void next.btn.offsetWidth;
      if (next.btn && isCheckoutButton(next.btn)) {
        next.checkoutSubmitted = submitCheckoutLeave(next.btn, doc);
      }
      /* If the page never actually leaves, hand the button back instead of leaving a
         spinner running forever with no way for the shopper to retry. Longer than the
         progress bar so a slow but working page is never interrupted. */
      leaveTimer = win.setTimeout(giveUpLeave, LEAVE_GIVE_UP_MS);
    }

    function leaveFromEvent(event) {
      if (modifiedClick(event)) return null;
      if (event.pointerType === "mouse" && event.button != null && event.button !== 0) {
        return null;
      }
      var target = eventElement(event);
      if (!target) return null;
      var cardHit = findCardLeave(target);
      if (cardHit && isLeaveHref(cardHit.link.href, win.location)) {
        return { card: cardHit.card, link: cardHit.link, btn: null };
      }
      var btn = findButtonLeave(target);
      if (!btn) return null;
      if (isCheckoutButton(btn)) {
        if (win.document.documentElement.classList.contains("cart-line-update-pending")) {
          return null;
        }
        return { card: null, link: null, btn: btn };
      }
      if (isLeaveHref(btn.href, win.location)) {
        return { card: null, link: btn, btn: btn };
      }
      return null;
    }

    function onLeaveIntent(event) {
      if (leaving) return;
      if (event.type === "touchstart" && event.pointerType) return;
      var next = leaveFromEvent(event);
      if (!next) return;
      var point = eventPoint(event);
      next.x = point ? point.x : 0;
      next.y = point ? point.y : 0;
      beginLeave(next);
    }

    function onLeaveMove(event) {
      if (!leaving || leaving.checkoutSubmitted) return;
      var point = eventPoint(event);
      if (!point) return;
      var dx = point.x - leaving.x;
      var dy = point.y - leaving.y;
      if (dx * dx + dy * dy > MOVE_CANCEL_PX2) clearLeave();
    }

    function onLeaveCancel() {
      if (leaving && !leaving.checkoutSubmitted) clearLeave();
    }

    function onPageShow(event) {
      if (event.persisted) clearLeave();
    }

    doc.addEventListener("pointerdown", onLeaveIntent, { capture: true, passive: true });
    doc.addEventListener("touchstart", onLeaveIntent, { capture: true, passive: true });
    doc.addEventListener("pointermove", onLeaveMove, { capture: true, passive: true });
    doc.addEventListener("touchmove", onLeaveMove, { capture: true, passive: true });
    doc.addEventListener("pointercancel", onLeaveCancel, { capture: true });
    doc.addEventListener("touchcancel", onLeaveCancel, { capture: true });
    doc.addEventListener("contextmenu", onLeaveCancel, { capture: true });
    win.addEventListener("pageshow", onPageShow);
  }

  return {
    MOVE_CANCEL_PX2: MOVE_CANCEL_PX2,
    SPINNER_HTML: SPINNER_HTML,
    isSamePageUrl: isSamePageUrl,
    isLeaveHref: isLeaveHref,
    findCardLeave: findCardLeave,
    findButtonLeave: findButtonLeave,
    isCheckoutButton: isCheckoutButton,
    submitCheckoutLeave: submitCheckoutLeave,
    bindTapLeave: bindTapLeave,
  };
});
